<template>
  <div class="w-100">
    <template v-if="user">
      <div class="headline d-flex flex-row align-items-center justify-content-between w-100 border-bottom pb-3">
        <h2 class="card-title font-weight-bolder">{{ $t('title.transactions_history') }}</h2>
      </div>
      <div class="row mt-4">
        <div class="col-12">
          <div class="questions sygni-grid">
            <div class="">
              <div class="row mt-2">
                <div class="col-12">
                  <div class="filters">
                    <div class="row">
                      <div class="col-4">
                        <filter-date-range-picker
                            :filter-query="filterQuery"
                            :locale="{firstDay: 1, format: 'yyyy-mm-dd'}"
                            :auto="true"
                            :linked="false"
                            :time-picker="true"
                            filter-name="createdAt"
                            open-direction="left"
                            :label="$t('filters.label.transaction_date')">
                        </filter-date-range-picker>
                      </div>
                      <div class="col-4">
                        <filter-input-with-operator
                            :filter-query="filterQuery"
                            :operator-options="filterOptions.comparisonOperator"
                            :operator-multiple="false"
                            :default-operator="{value: 'eq', text: 'equales'}"
                            :restrict-input="true"
                            restriction-type="decimals"
                            filter-name="quantity"
                            :label="$t('filters.label.quantity')"
                        >
                        </filter-input-with-operator>
                      </div>
                      <div class="col-4">
                        <filter-select
                            :filter-query="filterQuery"
                            :options="filterOptions.currency"
                            :multiple="false"
                            ref="currency-ref"
                            options-label="text"
                            options-track-by="value"
                            filter-name="walletCurrency"
                            :label="$t('filters.label.wallet_currency')"
                            :placeholder="$t('filters.placeholder.wallet_currency')">
                        </filter-select>
                      </div>


                    </div> <!-- #row-->

                    <div class="row">
                      <div class="col-4">
                        <filter-select
                            :filter-query="filterQuery"
                            :options="filterOptions.transactionType"
                            :multiple="false"
                            options-label="text"
                            options-track-by="value"
                            filter-name="transactionType"
                            :label="$t('filters.label.transaction_type')"
                            :placeholder="$t('filters.placeholder.transaction_type')">
                        </filter-select>
                      </div>
                      <div class="col-4">
                        <filter-input-with-operator
                            :filter-query="filterQuery"
                            :operator-options="filterOptions.comparisonOperator"
                            :operator-multiple="false"
                            :default-operator="{value: 'eq', text: 'equales'}"
                            :restrict-input="true"
                            restriction-type="decimals"
                            filter-name="transactionAmount"
                            :label="$t('filters.label.transaction_amount')"
                        >
                        </filter-input-with-operator>
                      </div>
                      <div class="col-4">
                        <filter-input
                            :filter-query="filterQuery"
                            :default-filter="$route.query.transactionId"
                            :filter-operator="'like'"
                            filter-name="transactionId"
                            :label="$t('filters.label.transaction_id')"
                        >
                        </filter-input>
                      </div>
                    </div> <!-- #row-->

                    <div class="row">
                      <div class="col-12">
                        <div class="float-right">
                          <b-button @click="resetFilters()" variant="secondary" class="mr-2">{{$t('filters.btn.clear')}}</b-button>
                          <b-button @click="applyFilters()" variant="primary">{{$t('filters.btn.apply')}}</b-button>
                        </div>
                      </div>
                    </div> <!-- #row-->
                  </div> <!-- #filters-->
                  <grid-pagination :total-count="totalCount"
                                   :per-page="perPage"
                                   :current-page="currentPage"
                                   @load-items="paginationReload"></grid-pagination>

                  <div class="table-responsive">
                    <b-table striped show-empty
                             :empty-text="$t('table.no_results')"
                             :busy="isBusy"
                             hover
                             head-variant="dark"
                             outlined
                             :sort-by.sync="orderColumn"
                             :sort-desc.sync="orderDirection"
                             :no-local-sorting="true"
                             :items="items"
                             :fields="fields">

                      <template #head(alias)="data">
                        <b-checkbox
                            @change="toggleAllCheckboxes($event, data)">
                        </b-checkbox>
                      </template>

                      <template #cell(alias)="data">
                        <b-checkbox v-model="selectedRows"
                                    :key="data.item.alias"
                                    :value="data.item.alias">
                        </b-checkbox>
                      </template>

                      <template #cell(transactionId)="data">
                        {{ data.value | shortId }}
                      </template>

                      <template #cell(languages)="data">
                        <div class="table-flag--wrapper">
                          <span v-for="(lang, index) in data.value" :key="index">

                          <img :src="`/admin/images/flags/${lang}.svg`" class="table-flags"> {{ lang.toUpperCase() }}

                          </span>
                        </div>
                      </template>

                      <template #cell(aliasId)="data">
                        <div>{{ data.item.alias }}</div>
                      </template>

                      <template #cell(createdAt)="data">
                        {{ data.value | dateFormat('YYYY-MM-DD HH:mm:ss') }}
                      </template>

                      <template #cell(transactionAmount)="data">
                        {{ data.value | numberFormat }}
                      </template>

                      <template #cell(categories)="data">
                        <div class="table-categories--wrapper" v-for="(cat, index) in data.value" :key="index">
                          <b-badge variant="light">{{ cat }}</b-badge>
                        </div>
                      </template>

                      <template #cell(walletCurrency)="data">
                        {{ $t(data.value) }}
                      </template>

                      <template #cell(paymentMethod)="data">
                        {{ $t(data.value) }}
                      </template>

                      <template #cell(paymentStatus)="data">
                        <b-badge pill variant="danger" v-if="data.value === 'REJECTED'">{{ $t(data.value) }}</b-badge>
                        <b-badge pill variant="warning" v-else-if="data.value === 'PENDING'">{{ $t(data.value) }}
                        </b-badge>
                        <b-badge pill variant="success" v-else>{{ $t(data.value) }}</b-badge>
                      </template>

                      <template #cell(transactionType)="data">
                        <b-badge pill variant="primary" v-if="data.value === 'PURCHASE'">{{ $t(data.value) }}</b-badge>
                        <b-badge pill variant="danger" v-else>{{ $t(data.value) }}</b-badge>
                      </template>

                      <template #cell(answers)="data">
                        <div class="table--answers">
                          <div v-for="(ans, index) in data.value" :key="index">
                            <b-badge :variant="data.item.correctAnswer === (index+1) ? 'success' : ''">
                              {{ ans }}
                            </b-badge>
                          </div>
                        </div>
                      </template>

                      <template #cell(actions)="data">
                        <div class="table-action--cell">
                          <template v-if="data.item.paymentStatus === 'PENDING'">
                            <b-button variant="success" size="sm"
                                      @click="confirmTransaction(data.item)">
                              {{$t('table.btn.confirm')}}
                            </b-button>
                            <b-button variant="danger" size="sm"
                                      @click="cancelTransaction(data.item)">
                              {{$t('table.btn.cancel')}}
                            </b-button>
                          </template>
                        </div>
                      </template>

                      <template #table-busy>
                        <div class="text-center text-danger my-2">
                          <b-spinner class="align-middle"></b-spinner>
                        </div>
                      </template>

                    </b-table>
                  </div> <!-- #table-responsive-->

                  <grid-pagination :total-count="totalCount"
                                   :per-page="perPage"
                                   :current-page="currentPage"
                                   @load-items="paginationReload"></grid-pagination>

                </div>
              </div>
            </div> <!-- #card-body-->
          </div> <!-- #card questions sygni-grid-->
        </div>
      </div>
    </template>
    <div class="spinner-wrapper" v-else>
      <b-spinner></b-spinner>
    </div>
  </div>

</template>

<script>

import axios from "axios";
import FilterDateRangePicker from "../../../components/grid/filters/FilterDateRangePicker";
import FilterInputWithOperator from "../../../components/grid/filters/FilterInputWithOperator";
import FilterInput from "../../../components/grid/filters/FilterInput";
import FilterSelect from "../../../components/grid/filters/FilterSelect";
import GridMixin from "../../../components/grid/GridMixin";
import GridPagination from "../../../components/grid/grid-pagination/GridPagination";

export default {
  mixins: [GridMixin],
  components: {
    FilterSelect,
    FilterInput,
    FilterInputWithOperator,
    FilterDateRangePicker,
    GridPagination
  },
  data() {
    return {
      gridGetUrl: '/api/transaction/',
      deleteActionUrl: '/api/dictionary/',
      showDeleted: false,
      orderColumn: null,
      orderDirection: true,
      transactionId: null,
      fields: [
        {key: 'transactionId', label: this.$t('table.header.transaction_id')},
        {key: 'createdAt', label: this.$t('table.header.transaction_date'), sortable: true},
        {key: 'userNick', label: this.$t('table.header.nick_name'), sortable: true},
        {key: 'userEmail', label: this.$t('table.header.email'), sortable: true},
        {key: 'transactionType', label: this.$t('table.header.transaction_type'), sortable: true},
        {key: 'quantity', label: this.$t('table.header.quantity'), sortable: true},
        {key: 'walletCurrency', label: this.$t('table.header.wallet_currency')},
        {key: 'transactionAmount', label: this.$t('table.header.amount'), sortable: true},
        {key: 'transactionCurrency', label: this.$t('table.header.currency')},
        {key: 'transactionStatus', label: this.$t('table.header.payment_status'), sortable: true},
        {key: 'paymentMethod', label: this.$t('table.header.payment_method')}
      ],
      selectOptions: {
        bulkActions: [
          {value: null, text: this.$t('table.bulk.actions')},
          {value: 'delete', text: this.$t('table.bulk.actions.delete')},
        ],
      },
      filterOptions: {
        comparisonOperator: [
          {value: 'eq', text: this.$t('operator.equales')},
          {value: 'gte', text: this.$t('operator.greater_then')},
          {value: 'lte', text: this.$t('operator.lower_than')},
          {value: 'btw', text: this.$t('operator.between')},
        ],
        status: [
          {value: 10, text: 'INITIAL'},
          {value: 20, text: 'EMAIL CONFIRMED'},
          {value: 30, text: 'REGISTRATION COMPLETED'}
        ],
        transactionType: [
          {value: 'PURCHASE', text: this.$t('PURCHASE')},
          {value: 'RESALE', text: this.$t('RESALE')},
        ],
        currency: [
          {value: 'FUN_COINS', text: this.$t('FUN_COINS')},
          {value: 'QUIZ_COINS', text: this.$t('QUIZ_COINS')},
        ],
        paymentStatus: [
          {value: 'CONFIRMED', text: 'CONFIRMED'},
          {value: 'REJECTED', text: 'REJECTED'},
          {value: 'PENDING', text: 'PENDING'},
        ],
      },
      items: [],
    };
  },
  created() {

  },
  mounted() {
    this.$on('refresh-dict', () => {
      this.loadItems();
    })
  },
  methods: {
    createFilterQuery() {
      let filter = '';
      Object.keys(this.filterQuery).forEach(key => {

        filter += this.filterQuery[key];
        if (key === 'walletCurrency') {
          filter += '&filters[walletCurrency][0][operator]=in'
        }
      })
      return filter;
    },
    confirmTransaction(item) {
      let _that = this;
      this.$bvModal.msgBoxConfirm('Transaction: ' + this.$options.filters.shortId(item.transactionId), {
        title: 'Are you sure to confirm transaction?',
        okTitle: 'Confirm',
        cancelTitle: 'Cancel',
        okVariant: 'success',
      })
          .then(value => {
            if (value) {
              axios.post(`/api/purchase/incoming-manual-money-payment/${item.transactionId}/confirmation`)
                  .then(function () {
                    _that.loadItems();
                  })
                  .catch(error => {
                    console.log(error);
                  });
            }
          });
    },
    cancelTransaction(item) {
      let _that = this;
      this.$bvModal.msgBoxConfirm('Transaction: ' + this.$options.filters.shortId(item.transactionId), {
        title: 'Are you sure to reject transaction?',
        okTitle: 'Confirm',
        cancelTitle: 'Cancel',
        okVariant: 'warning',
      })
          .then(value => {
            if (value) {
              axios.post(`/api/purchase/incoming-manual-money-payment/${item.transactionId}/rejection`)
                  .then(function () {
                    _that.loadItems();
                  })
                  .catch(error => {
                    console.log(error);
                  });
            }
          });
    },
    loadItems() {
      this.isBusy = true;
      const playerIdFromUrl = this.$route.params.id;
      if (this.user) {
        let _that = this;
        let offset = this.perPage * (this.currentPage - 1);
        let fullUrl = this.gridGetUrl + '?limit='
            + this.perPage
            + '&offset=' + offset
            + this.createSortQuery() + this.createFilterQuery()
            + `&filters[playerId][0][value]=${playerIdFromUrl}`;
        axios.get(fullUrl)
            .then(function (response) {
              _that.items = response.data.items;
              _that.totalCount = response.data.totalCount;
              _that.isBusy = false;
            })
            .catch(error => {
              console.log(error);
            });
      }
    },
    toggleAllCheckboxes: function (checked) {
      this.selectedRows = [];
      if (checked) {
        let _that = this;
        this.items.forEach((value) => {
          _that.selectedRows.push(value.id);
        })
      }
    },
    createNewDictModal() {
      // const
      //     ModalConstructor = Vue.extend(NewDictionaryModal),
      //     modal = new ModalConstructor({parent: this});
      //
      // modal.name = 'create-dictionary';
      // modal.title = 'Create dictionary';
      //
      // modal.$mount();
    },
    goTo(item) {
      this.$router.push({name: 'dictionary-entry', params: {entry: item.alias}});
    },
    edit(item) {
      this.$router.push({name: 'dictionary-entry', params: {entry: item.alias}});
    },
    deleteItem: function (item) {
      let _that = this;
      this.$bvModal.msgBoxConfirm('Dictionary: ' + item.alias, {
        title: 'Are you sure to delete dictionary?',
        okTitle: 'Delete',
        cancelTitle: 'Cancel',
        okVariant: 'danger',
      })
          .then(value => {
            if (value) {
              axios.delete(`/api/dictionary/${item.alias}`)
                  .then(function () {
                    _that.loadItems();
                  })
                  .catch(error => {
                    console.log(error);
                  });
            }
          });
    },
    editDictionary() {
      // const
      //     ModalConstructor = Vue.extend(DictionaryEditModal),
      //     modal = new ModalConstructor({parent: this});
      //
      // modal.name = 'edit-dict';
      // modal.title = 'Edit dictionary';
      // modal.form = {...dict};
      //
      // modal.$mount();
    },
  },
  computed: {
    user: function () {
      return this.$store.getters["players/player"];
    },
  },
  watch: {
    user: function (old, nn) {
      if (!nn) {
        setTimeout(() => this.applyFilters(), 20);
      }
    }
  },

}
</script>
